/**
 * Watchdog WebSocket Service
 *
 * WebSocket для live-обновлений дашборда мониторинга рабочих сессий.
 * Получает dashboard_update каждые 15 сек + push new_alert от watchdog.
 * Использует SharedWebSocket — один реальный коннект на пользователя,
 * остальные вкладки получают данные через BroadcastChannel.
 */

import { SharedWebSocket } from '../utils/shared-websocket';
import authService from './auth.service';
import { onAuthTokenRefreshed } from '../utils/authTokenEmitter';
import type { DashboardUpdateData } from '../types/watchdog.types';

type DashboardCallback = (data: DashboardUpdateData, timestamp: string) => void;
type ConnectionCallback = (connected: boolean) => void;
type SessionEventCallback = (event: 'session_invalidated') => void;

const LOG_PREFIX = '[WatchdogWS]';
// Exponential backoff с cap 30s: 1 → 2 → 4 → 8 → 16 → 30 → 30 ...
const BACKOFF_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000];
const MAX_RECONNECT_ATTEMPTS = 8;
const PING_INTERVAL_MS = 30_000;

function buildUrl(): string {
  const tokens = authService.getTokens();
  const token = tokens?.access_token ?? '';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/shifts/ws/work-sessions?token=${encodeURIComponent(token)}`;
}

class WatchdogWebSocketService {
  private sharedWs: SharedWebSocket | null = null;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempt = 0;
  private generation = 0;
  private shouldReconnect = false;

  private dashboardListeners = new Set<DashboardCallback>();
  private connectionListeners = new Set<ConnectionCallback>();
  private sessionListeners = new Set<SessionEventCallback>();

  constructor() {
    onAuthTokenRefreshed(() => {
      if (this.sharedWs !== null) {
        console.log(LOG_PREFIX, 'JWT обновлён — пересоздаём соединение');
        this.reconnect();
      }
    });
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  onDashboardUpdate(cb: DashboardCallback): () => void {
    this.dashboardListeners.add(cb);
    return () => this.dashboardListeners.delete(cb);
  }

  onConnectionChange(cb: ConnectionCallback): () => void {
    this.connectionListeners.add(cb);
    return () => this.connectionListeners.delete(cb);
  }

  onSessionEvent(cb: SessionEventCallback): () => void {
    this.sessionListeners.add(cb);
    return () => this.sessionListeners.delete(cb);
  }

  connect(): void {
    if (this.sharedWs !== null) return;
    this.shouldReconnect = true;
    this.reconnectAttempt = 0;
    this._createSharedWs();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.generation++;
    this._clearPingInterval();
    this._clearReconnectTimer();
    if (this.sharedWs) {
      this.sharedWs.close();
      this.sharedWs = null;
    }
    this.notifyConnection(false);
  }

  reconnect(): void {
    this.generation++;
    this._clearPingInterval();
    this._clearReconnectTimer();
    if (this.sharedWs) {
      this.sharedWs.close();
      this.sharedWs = null;
    }
    this.reconnectAttempt = 0;
    this._createSharedWs();
  }

  isConnected(): boolean {
    return this.sharedWs !== null && this.sharedWs.readyState === WebSocket.OPEN;
  }

  // ─── Internal ───────────────────────────────────────────────────────────

  private _createSharedWs(): void {
    const myGen = ++this.generation;
    const url = buildUrl();
    console.log(LOG_PREFIX, 'Подключение к', url.replace(/token=[^&]+/, 'token=***'));

    let ws: SharedWebSocket;
    try {
      ws = new SharedWebSocket(url);
    } catch (err) {
      console.error(LOG_PREFIX, 'Ошибка создания WebSocket:', err);
      this._scheduleReconnect(myGen);
      return;
    }
    this.sharedWs = ws;

    ws.onopen = () => {
      if (this.generation !== myGen) return;
      console.log(LOG_PREFIX, 'Соединение установлено');
      this.reconnectAttempt = 0;
      this.notifyConnection(true);
      this._startPingInterval();
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.generation !== myGen) return;
      try {
        const msg = JSON.parse(event.data as string);
        this.handleMessage(msg);
      } catch (err) {
        console.error(LOG_PREFIX, 'Ошибка парсинга:', err);
      }
    };

    ws.onerror = (event: Event) => {
      if (this.generation !== myGen) return;
      console.error(LOG_PREFIX, 'Ошибка соединения:', event);
    };

    ws.onclose = (event: CloseEvent) => {
      if (this.generation !== myGen) return;
      console.warn(LOG_PREFIX, 'Соединение закрыто:', { code: event.code, reason: event.reason });
      this._clearPingInterval();
      this.notifyConnection(false);

      if (event.code === 1008 || event.code === 4001) return;
      if (this.shouldReconnect) this._scheduleReconnect(myGen);
    };
  }

  private handleMessage(msg: { type?: string; event?: string; data?: unknown; timestamp?: string; detail?: string }): void {
    const msgType = msg.type || msg.event;

    switch (msgType) {
      case 'connected':
        console.log(LOG_PREFIX, 'Подтверждение подключения от сервера');
        break;

      case 'pong':
        // Keepalive reply
        break;

      case 'dashboard_update':
        if (msg.data) {
          const data = msg.data as DashboardUpdateData;
          const timestamp = msg.timestamp || new Date().toISOString();
          this.dashboardListeners.forEach(cb => {
            try { cb(data, timestamp); } catch (err) { console.error(LOG_PREFIX, 'Ошибка в dashboard listener:', err); }
          });
        }
        break;

      case 'session_invalidated':
        console.warn(LOG_PREFIX, 'Сессия инвалидирована');
        this.shouldReconnect = false;
        this.sessionListeners.forEach(cb => {
          try { cb('session_invalidated'); } catch { /* ignore */ }
        });
        if (this.sharedWs) {
          this.sharedWs.close();
          this.sharedWs = null;
        }
        break;

      case 'error':
        console.error(LOG_PREFIX, 'Ошибка от сервера:', msg.detail);
        break;

      default:
        console.log(LOG_PREFIX, 'Неизвестное событие:', msg);
    }
  }

  private _startPingInterval(): void {
    this._clearPingInterval();
    this.pingIntervalId = setInterval(() => {
      if (this.sharedWs) {
        try {
          this.sharedWs.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // connection might have dropped — onclose will handle
        }
      }
    }, PING_INTERVAL_MS);
  }

  private _clearPingInterval(): void {
    if (this.pingIntervalId !== null) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  private _scheduleReconnect(gen: number): void {
    this._clearReconnectTimer();

    if (this.reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.warn(LOG_PREFIX, `Превышен лимит переподключений (${MAX_RECONNECT_ATTEMPTS}). Остановка.`);
      this.shouldReconnect = false;
      this.sharedWs = null;
      return;
    }

    const delay = BACKOFF_DELAYS_MS[this.reconnectAttempt] ?? BACKOFF_DELAYS_MS[BACKOFF_DELAYS_MS.length - 1];
    this.reconnectAttempt++;
    console.log(LOG_PREFIX, `Переподключение через ${delay}мс (попытка ${this.reconnectAttempt}/${MAX_RECONNECT_ATTEMPTS})`);

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      if (this.generation !== gen) return;
      if (!this.shouldReconnect) return;
      this._createSharedWs();
    }, delay);
  }

  private _clearReconnectTimer(): void {
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
  }

  private notifyConnection(connected: boolean): void {
    this.connectionListeners.forEach(cb => {
      try { cb(connected); } catch { /* ignore */ }
    });
  }
}

const watchdogWebSocketService = new WatchdogWebSocketService();
export default watchdogWebSocketService;
